import type { ReactElement, ReactNode } from "react";
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type Announcements, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  BarChart3,
  BookOpen,
  Briefcase,
  CalendarDays,
  Gift,
  MessageCircleHeart,
  ClipboardCheck,
  Newspaper,
  Link2,
  ShoppingCart,
  ListOrdered,
  Hourglass,
  MessageSquareQuote,
  Users,
  ChevronDown,
  ChevronUp,
  Sparkles,
  CircleHelp,
  Clock,
  Eye,
  EyeOff,
  GripVertical,
  Images,
  MapPin,
  Megaphone,
  PanelBottom,
  PanelTop,
  Phone,
  Plus,
  Quote,
  ShoppingBag,
  Tags,
  Trash2,
  UtensilsCrossed,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";
import type { PublishIssue } from "@baze/api-client";
import { SECTION_GROUP_LABELS, SECTION_META, type SectionGroup, type SectionNode, type SectionType, type WebsiteDefinition } from "@baze/schema";
import { Button } from "@/components/ui/button";
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/cn";
import { t } from "@/i18n/id";
import { canAddSection, type PagePreset } from "./model/ops";
import { PagesBar } from "./pages-bar";

export const sectionIcons: Record<SectionType, LucideIcon> = {
  navbar: PanelTop,
  hero: Sparkles,
  about: BookOpen,
  services: Briefcase,
  menu: UtensilsCrossed,
  products: ShoppingBag,
  gallery: Images,
  testimonials: Quote,
  faq: CircleHelp,
  stats: BarChart3,
  "opening-hours": Clock,
  location: MapPin,
  contact: Phone,
  pricing: Tags,
  cta: Megaphone,
  timeline: ListOrdered,
  events: CalendarDays,
  countdown: Hourglass,
  team: Users,
  quote: MessageSquareQuote,
  gift: Gift,
  wishes: MessageCircleHeart,
  rsvp: ClipboardCheck,
  "blog-latest": Newspaper,
  links: Link2,
  picks: ShoppingCart,
  footer: PanelBottom,
};

const GROUPS: SectionGroup[] = ["konten", "bisnis", "konversi", "struktur"];


export function Outline({
  siteId,
  def,
  pageId,
  pageLimit,
  sections,
  selected,
  onPage,
  onAddPage,
  onRenamePage,
  onRemovePage,
  onSelect,
  onAdd,
  onMove,
  onReorder,
  onHide,
  onDelete,
  issues,
  suggested,
  event,
}: {
  siteId: string;
  def: WebsiteDefinition;
  pageId: string;
  pageLimit?: number;
  sections: SectionNode[];
  selected: string | null;
  onPage: (id: string) => void;
  onAddPage: (preset: PagePreset, title: string) => void;
  onRenamePage: (id: string, title: string) => void;
  onRemovePage: (id: string) => void;
  onSelect: (id: string) => void;
  onAdd: (type: SectionType) => void;
  onMove: (id: string, delta: -1 | 1) => void;
  onReorder: (from: number, to: number) => void;
  onHide: (id: string, hidden: boolean) => void;
  onDelete: (id: string) => void;
  /** Publish-check findings by section id, across all pages. */
  issues?: Map<string, PublishIssue[]>;
  suggested?: SectionType[];
  event?: boolean;
}) {
  const flagged = issues ? [...issues.keys()] : [];
  const page = def.pages.find((p) => p.id === pageId) ?? def.pages[0]!;
  const sensors = useSensors(
    // A small distance so a click on the row still selects it.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const ids = sections.map((s) => s.id);
  const label = (id: string | number) => {
    const s = sections.find((x) => x.id === id);
    return s ? SECTION_META[s.type].label : "";
  };
  const position = (id: string | number | undefined) => (id === undefined ? 0 : ids.indexOf(String(id)) + 1);
  const announcements: Announcements = {
    onDragStart: ({ active }) => t.editor.outline.dnd.start(label(active.id)),
    onDragOver: ({ active, over }) => (over ? t.editor.outline.dnd.over(label(active.id), position(over.id), ids.length) : undefined),
    onDragEnd: ({ active, over }) => (over ? t.editor.outline.dnd.end(label(active.id), position(over.id), ids.length) : undefined),
    onDragCancel: ({ active }) => t.editor.outline.dnd.cancel(label(active.id)),
  };
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    // reorderSections clamps the target between navbar and footer.
    onReorder(ids.indexOf(String(active.id)), ids.indexOf(String(over.id)));
  };
  return (
    <div className="flex h-full flex-col">
      <PagesBar siteId={siteId} def={def} pageId={page.id} pageLimit={pageLimit} onPage={onPage} onAdd={onAddPage} onRename={onRenamePage} onRemove={onRemovePage} />
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
        accessibility={{ announcements, screenReaderInstructions: { draggable: t.editor.outline.dnd.instructions } }}
      >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
      {flagged.length > 0 ? (
        // Jumps to the first section the publish check flags; the rows below carry a dot.
        <button type="button" onClick={() => onSelect(flagged[0]!)} className="mx-3 mt-3 flex items-center gap-2 rounded-xl bg-warning-soft/70 px-3 py-2 text-left text-[13px] font-semibold text-amber-900 ring-1 ring-inset ring-amber-600/20 hover:bg-warning-soft">
          <TriangleAlert className="size-4 shrink-0" aria-hidden="true" />
          {t.editor.issues(flagged.length)}
        </button>
      ) : null}
      <ul className="scrollbar-thin mt-3 min-h-0 flex-1 space-y-0.5 overflow-y-auto px-2 pb-2" aria-label={t.editor.outline.title}>
        {sections.map((s, index) => {
          const Icon = sectionIcons[s.type];
          const pinned = s.type === "navbar" || s.type === "footer";
          const active = selected === s.id;
          const heading = sectionHeading(s);
          const flag = issues?.get(s.id)?.[0];
          return (
            <SortableRow key={s.id} id={s.id} pinned={pinned}>
              {(handle) => (
              <div
                className={cn(
                  "group flex flex-wrap items-center gap-0.5 rounded-xl pr-1 transition-colors",
                  active ? "bg-primary-soft text-primary" : "text-slate-700 hover:bg-slate-50",
                  s.hidden && "opacity-60",
                )}
              >
                {pinned ? (
                  <Tooltip content={t.editor.outline.locked} side="top">
                    <span className="w-4 shrink-0 max-lg:w-9" aria-hidden="true" />
                  </Tooltip>
                ) : (
                  <button
                    type="button"
                    {...handle}
                    aria-label={t.editor.outline.drag(SECTION_META[s.type].label)}
                    className="grid h-8 w-4 shrink-0 cursor-grab touch-none place-items-center rounded text-slate-300 group-hover:text-slate-500 focus-visible:text-slate-600 active:cursor-grabbing max-lg:h-12 max-lg:w-9 max-lg:text-slate-400"
                  >
                    <GripVertical className="size-3.5" aria-hidden="true" />
                  </button>
                )}
                <button type="button" className="flex min-h-11 min-w-0 flex-1 items-center gap-2.5 rounded-xl px-1.5 text-left max-lg:min-h-13" onClick={() => onSelect(s.id)}>
                  <span className={cn("grid size-7 shrink-0 place-items-center rounded-lg", active ? "bg-white text-primary" : "bg-slate-100 text-slate-500")}>
                    <Icon className="size-3.5" aria-hidden="true" />
                  </span>
                  {/* The section's own words ("Mempelai") lead; the type name ("Tim & Profil") is the small line. */}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold">{heading ?? SECTION_META[s.type].label}</span>
                    {s.hidden ? (
                      <span className="block text-[11px] font-medium text-slate-400">{t.editor.outline.hidden}</span>
                    ) : heading ? (
                      <span className="block truncate text-[11px] font-medium text-slate-400">{SECTION_META[s.type].label}</span>
                    ) : null}
                  </span>
                  {flag ? (
                    <Tooltip content={flag.message} side="right">
                      <span className="mr-1 size-2 shrink-0 rounded-full bg-amber-500" role="img" aria-label={flag.message} />
                    </Tooltip>
                  ) : null}
                </button>
                {/* Actions only on the selected row, so names are never squeezed by hover icons. */}
                <div className={cn("basis-full items-center justify-end pb-1", active ? "flex" : "hidden")}>
                  <IconButton label={t.editor.toolbar.moveUp} disabled={pinned || index === 0} onClick={() => onMove(s.id, -1)}>
                    <ChevronUp aria-hidden="true" />
                  </IconButton>
                  <IconButton label={t.editor.toolbar.moveDown} disabled={pinned || index === sections.length - 1} onClick={() => onMove(s.id, 1)}>
                    <ChevronDown aria-hidden="true" />
                  </IconButton>
                  <IconButton label={s.hidden ? t.editor.toolbar.show : t.editor.toolbar.hide} onClick={() => onHide(s.id, !s.hidden)}>
                    {s.hidden ? <Eye aria-hidden="true" /> : <EyeOff aria-hidden="true" />}
                  </IconButton>
                  <IconButton label={t.editor.toolbar.delete} disabled={pinned} onClick={() => onDelete(s.id)}>
                    <Trash2 aria-hidden="true" />
                  </IconButton>
                </div>
              </div>
              )}
            </SortableRow>
          );
        })}
      </ul>
      </SortableContext>
      </DndContext>
      <div className="border-t border-line p-3 max-lg:hidden">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="secondary" size="sm" className="w-full">
              <Plus aria-hidden="true" />
              {t.editor.picker.title}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" side="top" className="w-[320px] p-2">
            <p className="px-2 pt-1 pb-2 text-xs text-slate-500">{t.editor.picker.subtitle}</p>
            <div className="scrollbar-thin max-h-[min(420px,60vh)] overflow-y-auto">
              <SectionPicker page={page} onAdd={onAdd} suggested={suggested} event={event} wrap={(node) => <PopoverClose asChild>{node}</PopoverClose>} />
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

/**
 * Section types grouped as in the "Tambah section" picker. `grid` lays them out as large tiles (mobile
 * sheet); `wrap` lets the popover close itself on pick.
 */
export function SectionPicker({
  page,
  onAdd,
  grid,
  suggested = [],
  event,
  wrap = (node) => node,
}: {
  page: WebsiteDefinition["pages"][number];
  onAdd: (type: SectionType) => void;
  grid?: boolean;
  /** Section types this kind of site's templates use, shown first; the rest follow in their groups. */
  suggested?: SectionType[];
  /** Invitation or event site: descriptions speak to guests. */
  event?: boolean;
  wrap?: (node: ReactElement) => ReactNode;
}) {
  const picked = new Set(suggested);
  const groups: Array<{ key: string; label: string; types: SectionType[] }> = [
    ...(suggested.length ? [{ key: "suggested", label: t.editor.picker.suggested, types: suggested }] : []),
    ...GROUPS.map((group) => ({
      key: group,
      label: SECTION_GROUP_LABELS[group],
      types: (Object.keys(SECTION_META) as SectionType[]).filter((type) => SECTION_META[type].group === group && !picked.has(type)),
    })),
  ].filter((g) => g.types.length > 0);
  return (
    <div className="space-y-3">
      {groups.map(({ key, label, types }) => {
        return (
          <div key={key}>
            <p className={cn("px-2 text-[11px] font-semibold tracking-wide uppercase", key === "suggested" ? "text-primary" : "text-slate-400")}>{label}</p>
            <ul className={cn("mt-1", grid && "grid grid-cols-2 gap-2 px-1")}>
              {types.map((type) => {
                const meta = SECTION_META[type];
                const Icon = sectionIcons[type];
                const exists = !canAddSection(page, type);
                return (
                  <li key={type}>
                    {wrap(
                      <button
                        type="button"
                        disabled={exists}
                        className={cn(
                          "flex w-full text-left disabled:cursor-not-allowed disabled:opacity-45",
                          grid
                            ? "h-full flex-col gap-2 rounded-2xl bg-slate-50 p-3 ring-1 ring-slate-900/5 active:bg-primary-soft"
                            : "items-start gap-2.5 rounded-xl px-2 py-2 hover:bg-slate-50",
                        )}
                        onClick={() => onAdd(type)}
                      >
                        <span className={cn("grid shrink-0 place-items-center rounded-lg text-slate-600", grid ? "size-9 bg-white ring-1 ring-slate-900/5" : "mt-0.5 size-7 bg-slate-100")}>
                          <Icon className={grid ? "size-4" : "size-3.5"} aria-hidden="true" />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-semibold text-slate-900">{meta.label}</span>
                          <span className={cn("block text-xs text-slate-500", grid && "line-clamp-2")}>
                            {exists ? t.editor.picker.singletonExists : ((event && t.editor.picker.eventDescriptions[type]) || meta.description)}
                          </span>
                        </span>
                      </button>,
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

/**
 * The section's own name in the owner's words: the small label above the title ("Mempelai", "Galeri"),
 * else the title. Heroes use the title, their eyebrow is a tagline ("Kami menikah").
 */
export function sectionHeading(s: SectionNode): string | null {
  if (s.type === "navbar" || s.type === "footer") return null;
  const c = s.content as { eyebrow?: unknown; title?: unknown };
  const clean = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : null);
  const eyebrow = s.type === "hero" ? null : clean(c.eyebrow);
  return eyebrow && eyebrow.length <= 28 ? eyebrow : clean(c.title);
}

/** A sortable <li>; navbar and footer are pinned (registered so indexes line up, but never draggable). */
function SortableRow({ id, pinned, children }: { id: string; pinned: boolean; children: (handle: Record<string, unknown>) => ReactNode }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: pinned });
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn("relative", isDragging && "z-10 rounded-xl bg-white shadow-lg ring-1 ring-slate-900/10")}
    >
      {children({ ref: setActivatorNodeRef, ...attributes, ...listeners })}
    </li>
  );
}

function IconButton({ label, disabled, onClick, children }: { label: string; disabled?: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <Tooltip content={label} side="top">
      <button
        type="button"
        aria-label={label}
        disabled={disabled}
        className="grid size-7 place-items-center rounded-lg text-slate-500 hover:bg-white hover:text-slate-900 disabled:opacity-30 max-lg:size-10 [&_svg]:size-3.5 max-lg:[&_svg]:size-4"
        onClick={onClick}
      >
        {children}
      </button>
    </Tooltip>
  );
}
